import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Paper, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';
import useImage from 'services/Hooks/useImage';

const useStyles = makeStyles(() => ({
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const CartWall = ({ wall, handleOpenDeleteWallModal, editablePanelTypeInventoryItems, index }) => {
  const { getPanelTypeImage } = useImage();

  const classes = useStyles();
  const { t } = useTranslation();
  // const headers = useMemo(() => {
  //   return wall?.cartWallInventoryItems?.map(cw => {
  //     return {
  //       ...cw,
  //       inventoryItem: editablePanelTypeInventoryItems.find(ii => ii.id === cw.inventoryItem_Id),
  //     };
  //   });
  // }, [editablePanelTypeInventoryItems, wall]);

  const numberOfPanelImages = wall?.images?.filter(
    i => i.fileType === FileTypeEnumValue.Photo && (!i.numberOfPanels || i.numberOfPanels === wall.numberOfPanels),
  );
  const imageSrc =
    numberOfPanelImages?.length > 0
      ? getPanelTypeImage(wall?.panelType_Id, numberOfPanelImages[0].id)
      : '/images/brp-logo.png';

  return (
    <Paper elevation={3} sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            width: 240,
            flexShrink: 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, flexShrink: 0 }}>
            <ThemedTooltip key={`item-${wall.id}`} title={t('removeFromCart')} placement="top">
              <Fab color="error" size="small" className={classes.squareFab} onClick={() => handleOpenDeleteWallModal(wall)}>
                <Delete />
              </Fab>
            </ThemedTooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Box
              component="img"
              src={imageSrc}
              alt={wall?.name}
              sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { md: 360, lg: 460 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
          }}>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2, mb: 0.25 }}>
            {wall?.name}
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{`${wall.numberOfPanels} ${t('panels')}`}</Typography>
          {wall?.cartWallInventoryItems?.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              {wall.cartWallInventoryItems.map(header => (
                <Typography key={`${header?.name || header?.id}-${header?.quantity}`} variant="body2" color="text.secondary">
                  {`${header?.quantity}X ${header?.name}`}
                </Typography>
              ))}
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 180,
            flexShrink: 0,
            ml: 'auto',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('unitPrice')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18, whiteSpace: 'nowrap' }}>
            ${wall?.price?.toFixed(2)} {wall.currencyName}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
            width: 175,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('quantity')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {wall.quantity}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 190,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('total')}
          </Typography>
          <Typography fontWeight={800} sx={{ fontSize: 22, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
            ${wall?.subTotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {wall.currencyName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          p: 2,
          gap: 1.5,
        }}>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={wall?.name}
            sx={{ maxWidth: '100%', maxHeight: 170, objectFit: 'contain' }}
          />
          <ThemedTooltip title={t('removeFromCart')} placement="top">
            <Fab
              color="error"
              size="small"
              className={classes.squareFab}
              sx={{ position: 'absolute', top: 0, right: 0 }}
              onClick={() => handleOpenDeleteWallModal(wall)}>
              <Delete />
            </Fab>
          </ThemedTooltip>
        </Box>

        <Box>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {wall?.name}
          </Typography>
          <Typography sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }}>
            {`${wall.numberOfPanels} ${t('panels')}`}
          </Typography>
        </Box>

        {wall?.cartWallInventoryItems?.length > 0 && (
          <>
            <Divider />
            {wall.cartWallInventoryItems.map(header => (
              <Typography key={`${header?.name || header?.id}-${header?.quantity}-m`} variant="body2" color="text.secondary">
                {`${header?.quantity}X ${header?.name}`}
              </Typography>
            ))}
          </>
        )}

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('unitPrice')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18, textAlign: 'right' }}>
            ${wall?.price?.toFixed(2)} {wall.currencyName}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('quantity')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18 }}>
            {wall.quantity}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pb: 0.5 }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('price')}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography fontWeight={800} sx={{ fontSize: 24, lineHeight: 1.1 }}>
              ${wall?.subTotal.toFixed(2)} {wall.currencyName}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CartWall;
